import * as React from 'react'
import Joi, { Schema } from 'joi'
import { t } from 'i18next'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, ManageSearch as WorkflowIcon } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { postWorkflow, putWorkflow } from '../../store/package/actionCreator'
import { clearFieldError } from '../../store/common/actionCreator'
import { ErrorsState, FormState, latinOnly, validateForm } from '../../function/validation'
import { genRandomHex, getSourceName } from '../../function/common'
import { Workflow, WorkflowType } from '../../store/package/reducer'
import { TableRef } from '../datatable/types'
import * as lists from '../../hook/usePackagePopup'
import sprite_ from '../../sass/sprite.module.sass'
import { SourceIcon } from '../source-icon/SourceIcon'
import { fieldHint, typeName } from './taxonomy'
import { MethodCard, SearchField, toItem, YouWillGet } from './WorkflowPreviewDialog'
import l from '../../page/SubscribersPageV2.module.css'
import s from './PopupWorkflowV2.module.css'

/*
 * Create / edit a search workflow — package_v2's dialog. The classic
 * PopupWorkflow (component/package/PopupWorkflow.tsx, ~2200 lines) is
 * untouched and still serves /package; this one keeps its fields, its rules
 * and its requests, and lays them out as sections with the workflow as the
 * user's Search page will show it on the right, updating as you type.
 *
 * The classic form kept one state array and one hand-written checkbox block
 * per type (sixty of them, all alike). Here a type maps to its list of sources
 * (SOURCES_BY_TYPE, built from the same lists in hook/usePackagePopup) and
 * one set holds what is ticked: what is saved is that set, restricted to the
 * type's list — exactly what the classic `sourcesByType[type]` sent.
 */

/* the classic form's schema (PopupWorkflow.tsx), unchanged */
const schema: Schema = Joi.object({
  type: Joi.allow('phone', 'name', 'post', 'deepweb', 'webint', 'web_data_search', 'fb_person_wall_keyword'),
  title: latinOnly.label(t('field.label.title')),
  subTitle: latinOnly.label(t('field.label.subTitle')),
  price: Joi.number().required().min(0.01).max(99).label(t('field.label.price')),
  fakeCheckPrice: Joi.allow(null),
  reportCountry: Joi.allow('profiler'),
  aiSummarizingPrice: Joi.allow(null),
  maxNum: Joi.allow(null),
  sources: Joi.array().min(1).label(t('field.label.sources')),
  input: latinOnly.max(16).label(t('field.label.input')),
  inputIP: Joi.allow(null).label(t('field.label.inputIP')),
  inputPhone: Joi.allow(null).label(t('field.label.inputPhone')),
  button: latinOnly.max(12).label(t('field.label.button')),
  color: Joi.string().required(),
  country: Joi.allow(null),
})

const L = lists as unknown as Record<string, string[]>
/* type -> the sources it offers (the classic form's per-type checkbox blocks) */
const SOURCES_BY_TYPE: Record<string, string[]> = {
  phone: L.phones, phone_list: L.phoneList, email_list: L.emailList, name: L.socials, name_api: L.nameApi,
  crolo_x: L.crolo_x, crolo_vk: L.crolo_vk, crolo_ok: L.crolo_ok, irbis_criminal: L.irbis_criminal,
  ssn_trace: L.ssn_trace, nat_crim_check: L.nat_crim_check, pep: L.pep, court_records: L.court_records,
  crypto_wallet_screening: L.cryptoWalletScreening, region_monitoring: L.regionMonitoring,
  region_monitoring_entities_extract: L.regionMonitoringEntitiesExtract, irbispro_conclude_report: L.irbisProConcludeReport,
  adid: L.adid, adid_by_phone: L.adid_by_phone, adid_location: L.adid_location, group_members: L.group_members,
  irbis_tiktok: L.irbis_tiktok, bluesky_v3: L.bluesky, post: L.postLookups, deepweb: L.deepWebLookups,
  sentiment_analysys: L.sentimentAnalysys, kyc: L.kyc, web_collection: L.web_collection, ip_geo: L.ip_geo,
  ipgeo_number: L.geolocation_by_number, name_by_country: L.nameByCountry, irbis_eye: L.findClone, photo_searcher: L.sniperAPI,
  web_data_search: L.advancedCollectionLookups, irbis_facebook: L.fbMobileCollectorLookups, irbis_instagram: L.instaMobileCollectorLookups,
  api_v2_x: L.api_v2_x, api_v2_instagram: L.api_v2_instagram, api_v2_linkedin: L.api_v2_linkedin,
  sn_api_profiler: L.sn_api_profile, sn_api_group_profiler: L.sn_api_group_profile, fb_person_wall_keyword: L.advCollectionPersonWallLookups,
  psycho_profile: L.psychoProfile, psycho_profile_summary: L.psychoProfile, image_insize: L.image_insize, profiler: L.profiler,
  sanctions_person_company: L.openSanctions, sanctions_pep: L.openSanctionsPep, watchlists: L.openSanctionsRegulatory,
  posts_by_keywords: L.postsByKeywords, posts_comments: L.postsComments, lookup_phone_verification: L.lookup_phone_verification,
  public_company_info: L.publicCompanyInfo, combined_phone: L.combinedPhones, combined_email: L.combinedEmails,
  combined_name: L.combinedNames, combined_id: L.combinedIds, face_search: L.faceSearches,
  lookup_phone_name_verification: L.combinedPhones, real_phone: L.combinedPhones,
}
/* types whose sources are fixed — nothing to pick */
const FIXED_SOURCES: Record<string, string[]> = {
  sn_api_profiler_fb_friends: ['osintFbFriends'], sn_api_profiler_fb_posts: ['osintFbPosts'],
  sn_api_profiler_insta_friends: ['osintInstaFriends'], sn_api_profiler_insta_posts: ['osintInstaPosts'],
}
/* combined_name: these sit apart, under "Irbis Social" (sprite without _api) */
const IRBIS_SOCIAL = ['fb_api', 'instagram_api', 'vk_api', 'ok_api', 'x_api', 'linkedin_api', 'tiktok_api', 'blsk_api', 'webmii_social', 'breach_name', 'snapchat']
const WEBINT_PARAMS = ['quotaprofiler-lookup', 'quotaprofiler-crawler', 'quotaprofiler-cases', 'quotaprofiler-periodic', 'quotaprofiler-monitoring',
  'quotawebint-lookup', 'quotawebint-crawler', 'quotawebint-cases', 'quotawebint-periodic', 'quotawebint-monitoring']
const AI_SUMMARY_TYPES = ['irbis_facebook', 'irbis_instagram', 'web_data_search', 'fb_person_wall_keyword', 'crolo_x', 'crolo_vk', 'crolo_ok',
  'irbis_tiktok', 'bluesky_v3', 'api_v2_x', 'api_v2_instagram', 'api_v2_linkedin', 'ssn_trace', 'nat_crim_check', 'pep', 'court_records']

const TYPES: [string, string][] = [
  ['phone', 'Phone'], ['name', 'Name (Crawlers)'], ['name_api', 'Name Api (IRBIS SN)'], ['post', 'Post'],
  ['posts_by_keywords', 'Posts by keywords (IRBIS SN)'], ['deepweb', 'Deep WEB'], ['webint', 'WebInt'],
  ['combined_phone', 'Advanced Phone Lookup'], ['phone_list', 'Advanced Phone Lookup (List)'], ['combined_email', 'Advanced E-mail Lookup'],
  ['email_list', 'Advanced E-mail Lookup (List)'], ['combined_name', 'Advanced Name Lookup'], ['name_by_country', 'Advanced Name Lookup By Country'],
  ['combined_id', 'Advanced ID Lookup'], ['face_search', 'Face Search'], ['additional_quotas', 'Add Quotas'],
  ['web_data_search', 'Facebook API (Crawlers)'], ['irbis_facebook', 'Facebook API (IRBIS SN)'], ['irbis_instagram', 'Instagram API (IRBIS SN)'],
  ['crolo_x', 'X API (IRBIS SN)'], ['crolo_vk', 'Vk API (IRBIS SN)'], ['crolo_ok', 'Ok API (IRBIS SN)'], ['irbis_tiktok', 'TikTok API (IRBIS SN)'],
  ['bluesky_v3', 'BlueSky API (IRBIS SN)'], ['fb_person_wall_keyword', 'Facebook API (person wall keyword, Crawlers)'],
  ['sentiment_analysys', 'Sentiment Analysys'], ['irbis_eye', 'Irbis Eye'], ['image_insize', 'Photo Intel'], ['photo_searcher', 'Sniper API'],
  ['kyc', 'KYC'], ['web_collection', 'WEB Collection'], ['ip_geo', 'Geolocation'], ['ipgeo_number', 'Geolocation By Phone'],
  ['psycho_profile', 'PsychoProfile and Summary'], ['api_v2_x', 'API v.2 - X (Twitter)'], ['api_v2_instagram', 'API v.2 - Instagram'],
  ['api_v2_linkedin', 'API v.2 - LinkedIn'], ['lookup_phone_verification', 'Validate Phone Lookup Results'],
  ['lookup_phone_name_verification', 'Validate Phone Lookup By Name'], ['real_phone', 'Real Phone'],
  ['irbis_criminal', 'Irbis Criminal Checks (Out of date)'], ['ssn_trace', 'Social Security Number Trace'],
  ['nat_crim_check', 'National Criminal Screening'], ['pep', 'Politically Exposed Persons'],
  ['sanctions_person_company', 'Open Sanctions (Person\\Company)'], ['sanctions_pep', 'Open Sanctions (PEP)'],
  ['watchlists', 'Open Sanctions (Watchlists)'], ['court_records', 'Court Records'], ['crypto_wallet_screening', 'Crypto Wallet Screening'],
  ['region_monitoring', 'Region Monitoring'], ['region_monitoring_entities_extract', 'Region Monitoring (Entities Extract)'],
  ['irbispro_conclude_report', 'Irbis Pro Conclude Report'], ['adid', 'AdID (get AdID by coordinates)'],
  ['adid_by_phone', 'AdID (get AdId by phone number)'], ['adid_location', 'AdID (get location by AdId)'], ['profiler', 'Irbis Profiler'],
  ['group_members', 'Group Members Info'], ['psycho_profile_summary', 'OSINT: PsychoProfile'],
  ['public_company_info', 'Public Information about a company'],
  ['sn_api_profiler', 'OSINT: Social Network API Profiler (Common Profile Builder)'],
  ['sn_api_profiler_fb_friends', 'OSINT: Social Network API Profiler (Facebook Friends)'],
  ['sn_api_profiler_fb_posts', 'OSINT: Social Network API Profiler (Facebook Posts)'],
  ['sn_api_profiler_insta_friends', 'OSINT: Social Network API Profiler (Instagram Friends)'],
  ['sn_api_profiler_insta_posts', 'OSINT: Social Network API Profiler (Instagram Posts)'],
  ['sn_api_group_profiler', 'WEBINT: Social Network API Profiler (Group Profile Builder)'], ['posts_comments', 'Posts and Comments'],
]

/* the quota fields of a webint / additional_quotas workflow, read from its JSON `input` */
const QUOTA_KEYS = {
  webint: [
    ['Quota · Profiler', [['quotaProfilerLookup', 'Phone numbers'], ['quotaProfilerCrawlers', 'Social networks'], ['quotaProfilerCases', 'Cases']]],
    ['Quota · WebInt', [['quotaWebIntCrawlers', 'Tasks'], ['quotaWebIntCases', 'Cases'], ['quotaWebIntMonitoring', 'Monitored groups']]],
    ['Quota', [['quotaWebIntPeriodic', 'Expires in (days)']]],
    ['Trial · Profiler', [['quotaTrialProfilerLookup', 'Phone numbers'], ['quotaTrialProfilerCrawlers', 'Social networks'], ['quotaTrialProfilerCases', 'Cases']]],
    ['Trial · WebInt', [['quotaTrialWebIntCrawlers', 'Tasks'], ['quotaTrialWebIntCases', 'Cases'], ['quotaTrialWebIntMonitoring', 'Monitored groups']]],
    ['Trial', [['quotaTrialWebIntPeriodic', 'Expires in (days)']]],
  ],
  additional_quotas: [
    ['Additional · Profiler', [['additionalProfilerLookup', 'Phone numbers'], ['additionalProfilerCrawlers', 'Social networks'], ['additionalProfilerCases', 'Cases']]],
    ['Additional · WebInt', [['additionalWebIntCrawlers', 'Tasks'], ['additionalWebIntCases', 'Cases'], ['additionalWebIntMonitoring', 'Monitored groups']]],
  ],
} as const
/* every key the classic form sends, with the classic defaults */
const WEBINT_ALL = ['quotaProfilerLookup', 'quotaProfilerCrawlers', 'quotaProfilerCases', 'quotaProfilerMonitoring', 'quotaProfilerPeriodic',
  'quotaWebIntLookup', 'quotaWebIntCrawlers', 'quotaWebIntCases', 'quotaWebIntMonitoring', 'quotaWebIntPeriodic',
  'quotaTrialProfilerLookup', 'quotaTrialProfilerCrawlers', 'quotaTrialProfilerCases', 'quotaTrialWebIntCrawlers', 'quotaTrialWebIntCases',
  'quotaTrialWebIntMonitoring', 'quotaTrialWebIntPeriodic']
const ADDITIONAL_ALL = ['additionalProfilerLookup', 'additionalProfilerCrawlers', 'additionalProfilerCases', 'additionalProfilerMonitoring',
  'additionalProfilerPeriodic', 'additionalWebIntLookup', 'additionalWebIntCrawlers', 'additionalWebIntCases', 'additionalWebIntMonitoring']

function Field({ label, required, error, hint, children }: { label: string; required?: boolean; error?: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <label className={s.field}>
      <span className={s.label}>{label}{required && <i>*</i>}</span>
      {children}
      {error ? <span className={s.err}>{error}</span> : hint ? <span className={s.hint}>{hint}</span> : null}
    </label>
  )
}
function Section({ title, sub, children, right }: { title: string; sub?: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <div><b>{title}</b>{sub && <span>{sub}</span>}</div>
        {right}
      </div>
      <div className={s.sectionBody}>{children}</div>
    </section>
  )
}
function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" className={`${s.toggle} ${on ? s.toggleOn : ''}`} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className={s.switch}><span /></span>{label}
    </button>
  )
}

type Props = {
  mode: 'create' | 'edit'
  tableRef: React.RefObject<TableRef>
  onClose: () => void
  data?: Workflow
  theme: 'light' | 'dark'
}

export function PopupWorkflowV2({ mode, tableRef, onClose, data, theme }: Props) {
  const dispatch = useDispatch()
  const d = data as any

  const [type, setType] = React.useState<string>(data?.type || 'phone')
  const [title, setTitle] = React.useState(data?.title || '')
  const [subTitle, setSubTitle] = React.useState(data?.subTitle || '')
  const [price, setPrice] = React.useState<number>(data?.price || 1)
  const [maxNum, setMaxNum] = React.useState<number>(data?.maxNum || 0)
  const [isFakeCheck, setIsFakeCheck] = React.useState(!!data?.fakeCheckPrice)
  const [fakeCheckPrice, setFakeCheckPrice] = React.useState<number>(data?.fakeCheckPrice || 0)
  const [isAiSummarizing, setIsAiSummarizing] = React.useState(!!data?.aiSummarizingPrice)
  const [aiSummarizingPrice, setAiSummarizingPrice] = React.useState<number>(data?.aiSummarizingPrice || 0)
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set((data?.sources || []).map(String)))
  const [sourceQuery, setSourceQuery] = React.useState('')
  // ipgeo_number keeps {phone, ip} in `input`; everything else a plain hint
  const parsedInput = React.useMemo(() => { try { return JSON.parse(data?.input || '') } catch { return null } }, [data])
  const [input, setInput] = React.useState<string>(data?.type === 'ipgeo_number' && parsedInput?.phone ? parsedInput.phone : (data?.input || ''))
  const [inputIP, setInputIP] = React.useState<string>(data?.type === 'ipgeo_number' && parsedInput?.ip ? parsedInput.ip : (data?.input || ''))
  const [reportCountry, setReportCountry] = React.useState<'US' | 'IL' | ''>(
    data?.type === 'profiler' && (parsedInput?.reportCountry === 'US' || parsedInput?.reportCountry === 'IL') ? parsedInput.reportCountry : '',
  )
  const [quotas, setQuotas] = React.useState<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    ;[...WEBINT_ALL, ...ADDITIONAL_ALL].forEach((k) => {
      out[k] = (data?.type === 'webint' || data?.type === 'additional_quotas') && parsedInput && k in parsedInput ? String(parsedInput[k]) : '1'
    })
    return out
  })
  const [button, setButton] = React.useState(data?.button || '')
  const [color, setColor] = React.useState<string>(String(data?.color || genRandomHex(6)))
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => {
    setErrors(null)
    dispatch(clearFieldError)
  }, [dispatch, type, title, subTitle, price, maxNum, fakeCheckPrice, aiSummarizingPrice, picked, input, inputIP, reportCountry, quotas, button, color])

  const isWebint = type === 'webint'
  const isAdditional = type === 'additional_quotas'
  const isQuota = isWebint || isAdditional
  const typeSources = SOURCES_BY_TYPE[type] || []
  const fixed = FIXED_SOURCES[type]
  const sources: string[] = isQuota ? WEBINT_PARAMS : fixed || typeSources.filter((x) => picked.has(x))

  const changeType = (v: string) => { setType(v); setPicked(new Set()) }
  const toggleSource = (src: string) => setPicked((set) => {
    const next = new Set(set)
    if (next.has(src)) next.delete(src); else next.add(src)
    return next
  })
  const setAll = (list: string[], on: boolean) => setPicked((set) => {
    const next = new Set(set)
    list.forEach((x) => (on ? next.add(x) : next.delete(x)))
    return next
  })

  const requestData: FormState = {
    type,
    price,
    sources,
    fakeCheckPrice: isFakeCheck ? fakeCheckPrice : null,
    reportCountry: reportCountry || null,
    aiSummarizingPrice,
    maxNum,
    color,
    title: title.trim(),
    subTitle: subTitle.trim(),
    input: input.trim(),
    inputIP: inputIP.trim(),
    button: button.trim(),
  }

  function save() {
    // the classic form does not validate webint, add-quotas and profiler workflows
    if (!isQuota && type !== 'profiler') {
      const error = validateForm(schema, requestData)
      if (error) { setErrors(error); return }
    }
    const body: FormState = { ...requestData }
    if (isAdditional) body.price = 0
    // the classic form sends the unchecked fake-check price as it stands (0), not null
    if (!isFakeCheck) body.fakeCheckPrice = fakeCheckPrice
    if (isWebint) body.input = JSON.stringify(Object.fromEntries(WEBINT_ALL.map((k) => [k, quotas[k]])))
    if (isAdditional) body.input = JSON.stringify({ ...Object.fromEntries(ADDITIONAL_ALL.map((k) => [k, quotas[k]])), additionalWebIntPeriodic: '0' })
    if (type === 'profiler') body.input = JSON.stringify({ reportCountry: reportCountry || null })
    const done = () => tableRef.current?.refresh()
    if (mode === 'create') dispatch(postWorkflow(body, done))
    else dispatch(putWorkflow(body, data?.id as number, done))
    onClose()
  }

  const err = (k: string) => (errors as any)?.[k]
  const showInput = !isQuota && type !== 'web_data_search' && type !== 'fb_person_wall_keyword' && type !== 'ipgeo_number' && type !== 'profiler'

  // the source picker: one group, or for combined_name the regular ones and "Irbis Social"
  const groups: [string, string[]][] = type === 'combined_name'
    ? [['Sources', typeSources.filter((x) => !IRBIS_SOCIAL.includes(x))], ['Irbis Social', typeSources.filter((x) => IRBIS_SOCIAL.includes(x))]]
    : [['Sources', typeSources]]
  const sq = sourceQuery.trim().toLowerCase()

  // the user's view of what is being edited
  const previewWf = { ...(data || {}), id: data?.id ?? -1, type, title: title || 'Untitled workflow', subTitle, price, input, button, color, sources } as unknown as Workflow
  const item = toItem(previewWf)
  const previewPrice = `€${price}`
  const placeholder = fieldHint(item.kind, undefined, type === 'ipgeo_number' ? input : (showInput ? input : undefined))

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.icon} style={{ background: color }}><WorkflowIcon sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}>
            <b>{mode === 'create' ? 'Create search workflow' : `Edit search workflow: ${data?.title || ''}`}</b>
            <span>The card on the right is what users see on the Search page.</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.main}>
          <div className={s.form}>
            <Section title="Workflow" sub="Type, name and price">
              <Field label="Type">
                <select className={s.input} value={type} onChange={(e) => changeType(e.target.value)}>
                  {TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </Field>
              <div className={s.grid2}>
                <Field label="Title" required error={err('title')}>
                  <input className={s.input} value={title} placeholder={String(t('field.hint.title'))} onChange={(e) => setTitle(e.target.value)} />
                </Field>
                <Field label="Subtitle" required error={err('subTitle')} hint="The line under the title on the card">
                  <input className={s.input} value={subTitle} placeholder={String(t('field.hint.subtitle'))} onChange={(e) => setSubTitle(e.target.value)} />
                </Field>
              </div>
              {!isQuota && (
                <div className={s.grid3}>
                  <Field label="Price, €" required error={err('price')} hint="0.01 – 99">
                    <input className={s.input} type="number" min={0} max={99} step="0.01" value={price} onChange={(e) => setPrice(Number(e.target.value))} />
                  </Field>
                  {(type === 'phone_list' || type === 'email_list') && (
                    <Field label={type === 'phone_list' ? 'Max numbers in list' : 'Max e-mails in list'} required>
                      <input className={s.input} type="number" value={maxNum} onChange={(e) => setMaxNum(Number(e.target.value))} />
                    </Field>
                  )}
                  {type === 'profiler' && (
                    <Field label="Report country" required>
                      <select className={s.input} value={reportCountry} onChange={(e) => setReportCountry(e.target.value as 'US' | 'IL' | '')}>
                        <option value="">—</option>
                        <option value="US">🇺🇸 USA</option>
                        <option value="IL">🇮🇱 Israel</option>
                      </select>
                    </Field>
                  )}
                </div>
              )}
              {type === 'combined_phone' && (
                <div className={s.toggleRow}>
                  <Toggle on={isFakeCheck} onChange={(v) => { setIsFakeCheck(v); if (!v) { setFakeCheckPrice(0); setAiSummarizingPrice(0) } }} label="Fake Check price" />
                  {isFakeCheck && <input className={`${s.input} ${s.small}`} type="number" min={0} max={99} value={fakeCheckPrice} onChange={(e) => setFakeCheckPrice(Number(e.target.value))} />}
                </div>
              )}
              {AI_SUMMARY_TYPES.includes(type) && (
                <div className={s.toggleRow}>
                  <Toggle on={isAiSummarizing} onChange={(v) => { setIsAiSummarizing(v); if (!v) setFakeCheckPrice(0) }} label="AI data summarizing, €" />
                  {isAiSummarizing && <input className={`${s.input} ${s.small}`} type="number" min={0} max={99} value={aiSummarizingPrice} onChange={(e) => setAiSummarizingPrice(Number(e.target.value))} />}
                </div>
              )}
            </Section>

            {isQuota ? (
              <Section title={isWebint ? 'Quotas' : 'Additional quotas'} sub={isWebint ? 'What a WebInt package gives, and its trial' : 'Added on top of a WebInt package'}>
                <div className={s.quotaGrid}>
                  {QUOTA_KEYS[isWebint ? 'webint' : 'additional_quotas'].map(([group, fields]) => (
                    <div key={group} className={s.quotaGroup}>
                      <b>{group}</b>
                      {fields.map(([key, label]) => (
                        <Field key={key} label={label}>
                          <input className={s.input} type="number" min={1} value={quotas[key]} onChange={(e) => setQuotas((q) => ({ ...q, [key]: e.target.value }))} />
                        </Field>
                      ))}
                    </div>
                  ))}
                </div>
              </Section>
            ) : fixed ? (
              <Section title="Sources" sub="Fixed for this type">
                <div className={s.fixed}>{fixed.join(', ')}</div>
              </Section>
            ) : (
              <Section
                title="Sources"
                sub={`${sources.length} of ${typeSources.length} selected`}
                right={typeSources.length > 8 ? (
                  <input className={`${s.input} ${s.sourceSearch}`} value={sourceQuery} placeholder="Filter sources" onChange={(e) => setSourceQuery(e.target.value)} />
                ) : undefined}
              >
                {err('sources') && <span className={s.err}>{err('sources')}</span>}
                {groups.map(([name, list]) => {
                  const shown = list.filter((src) => !sq || src.toLowerCase().includes(sq) || String(getSourceName(src) || '').toLowerCase().includes(sq))
                  if (!list.length) return null
                  const all = list.every((x) => picked.has(x))
                  return (
                    <div key={name} className={s.group}>
                      <div className={s.groupHead}>
                        <span>{name}</span>
                        <button type="button" className={s.link} onClick={() => setAll(list, !all)}>{all ? 'Clear' : 'Select all'}</button>
                      </div>
                      <div className={s.sources}>
                        {shown.map((src) => {
                          const on = picked.has(src)
                          const spriteKey = `social__small_${name === 'Irbis Social' ? src.replace('_api', '') : src}`
                          return (
                            <button key={src} type="button" className={`${s.source} ${on ? s.sourceOn : ''}`} onClick={() => toggleSource(src)}>
                              <span className={s.check}>{on ? '✓' : ''}</span>
                              <SourceIcon source={src} fallback={<span className={(sprite_ as Record<string, string>)[spriteKey] || s.noIcon} />} />
                              <span className={s.sourceName}>{getSourceName(src) || src}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </Section>
            )}

            <Section title="Search field" sub="What the user types and the button">
              <div className={s.grid2}>
                {showInput && (
                  <Field label="Input hint" required error={err('input')} hint="Up to 16 characters">
                    <input className={s.input} value={input} placeholder={String(t('field.hint.input'))} onChange={(e) => setInput(e.target.value)} />
                  </Field>
                )}
                {type === 'ipgeo_number' && (
                  <>
                    <Field label={String(t('field.label.inputPhone'))} required error={err('input')}>
                      <input className={s.input} value={input} placeholder={String(t('field.hint.inputPhone'))} onChange={(e) => setInput(e.target.value)} />
                    </Field>
                    <Field label={String(t('field.label.inputIP'))} required error={err('inputIP')}>
                      <input className={s.input} value={inputIP} placeholder={String(t('field.hint.inputIP'))} onChange={(e) => setInputIP(e.target.value)} />
                    </Field>
                  </>
                )}
                {(type === 'web_data_search' || type === 'fb_person_wall_keyword') && (
                  <Field label={String(t('field.label.limit'))} required error={err('input')}>
                    <input className={s.input} value={input} placeholder={String(t('field.hint.limit'))} onChange={(e) => setInput(e.target.value)} />
                  </Field>
                )}
                <Field label="Button text" required error={err('button')} hint="Up to 12 characters">
                  <input className={s.input} value={button} placeholder={String(t('field.hint.text'))} onChange={(e) => setButton(e.target.value)} />
                </Field>
              </div>
              <Field label="Color" error={err('color')}>
                <div className={s.colorRow}>
                  <input type="color" className={s.colorPick} value={/^#[0-9a-f]{6}$/i.test(color) ? color : '#2D89F4'} onChange={(e) => setColor(e.target.value)} />
                  <input className={`${s.input} ${s.mono}`} value={color} onChange={(e) => setColor(e.target.value)} />
                  <button type="button" className={s.ghostSmall} onClick={() => setColor(genRandomHex(6))}>Random</button>
                </div>
              </Field>
            </Section>
          </div>

          <aside className={s.preview}>
            <div className={s.previewHead}>Preview · {typeName(type)}</div>
            <div className={s.previewBody}>
              <MethodCard option={{ key: 'this', item, label: item.title, kind: item.kind }} active isThis price={previewPrice} />
              <SearchField placeholder={placeholder} button={button} />
              <YouWillGet item={item} price={previewPrice} />
            </div>
          </aside>
        </div>

        <div className={s.foot}>
          {errors && <span className={s.footErr}>Some fields need attention</span>}
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.primary} onClick={save}>{mode === 'create' ? 'Create workflow' : 'Save changes'}</button>
        </div>
      </div>
    </Dialog>
  )
}
